/**
 * CheckoutSuccessPage — shown after a successful Stripe checkout session.
 * URL: /checkout/success (linked from Stripe's return_url parameter).
 *
 * React Query re-fetches the subscription so the UI reflects the new plan
 * without requiring a full reload. Redirects to /dashboard after 6 seconds.
 */
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import { billingApi } from '@/lib/api'
import { ArrowRight, Star, Loader2 } from 'lucide-react'

const REDIRECT_DELAY_MS = 6_000

const PREMIUM_FEATURES = [
  'Unlimited Muhurta lookups',
  'Full Dasha interpretation',
  'Kundali compatibility analysis',
  'Personalised remedies',
  'AI-powered life insights',
  'iCal export for auspicious times',
]

// Deterministic star burst particles — 16 rays at fixed positions/delays
const BURST_PARTICLES = Array.from({ length: 16 }, (_, i) => ({
  angle: (360 / 16) * i,
  delay: (i % 4) * 0.1,
  size: i % 3 === 0 ? 1.5 : 1,
  color: i % 4 === 0 ? 'rgba(245,158,11,0.8)' : i % 4 === 1 ? 'rgba(167,139,250,0.7)' : 'rgba(200,200,240,0.5)',
}))

// Ambient twinkle stars scattered around the success card
const AMBIENT_STARS = [
  { top: '10%', left: '8%',  size: 4, delay: 0 },
  { top: '20%', left: '88%', size: 3, delay: 0.4 },
  { top: '35%', left: '5%',  size: 5, delay: 0.8 },
  { top: '50%', left: '92%', size: 3, delay: 0.2 },
  { top: '65%', left: '7%',  size: 4, delay: 1.2 },
  { top: '78%', left: '85%', size: 5, delay: 0.6 },
  { top: '88%', left: '15%', size: 3, delay: 1.0 },
  { top: '15%', left: '50%', size: 4, delay: 0.3 },
]

export function CheckoutSuccessPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [countdown, setCountdown] = useState(Math.round(REDIRECT_DELAY_MS / 1000))
  const [burstDone, setBurstDone] = useState(false)

  // Poll subscription until the Stripe webhook confirms active state.
  // refetchInterval returns false once active, stopping the polling loop.
  const { data: subscription } = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: () => billingApi.getSubscription(),
    refetchInterval: (query) =>
      query.state.data?.state === 'active' ? false : 2000,
  })
  const syncing = !subscription || subscription.state !== 'active'

  // Broadcast cache invalidation so other observers (Header, gates) update too
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['billing'] })
  }, [queryClient])

  // Hide burst particles after animation completes (~1.2s)
  useEffect(() => {
    const t = setTimeout(() => setBurstDone(true), 1400)
    return () => clearTimeout(t)
  }, [])

  // Auto-redirect countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(interval)
          navigate('/dashboard', { replace: true })
          return 0
        }
        return c - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [navigate])

  return (
    <div className="min-h-screen bg-cosmic-void flex items-center justify-center p-6 overflow-hidden">
      {/* Background: star-field + layered radial glows */}
      <div className="fixed inset-0 pointer-events-none star-field-overlay" aria-hidden="true" />
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 60% 50% at 50% 30%, rgba(107,33,168,0.22) 0%, transparent 70%),
            radial-gradient(ellipse 40% 40% at 80% 80%, rgba(59,7,100,0.18) 0%, transparent 60%)
          `,
        }}
        aria-hidden="true"
      />

      {/* Ambient twinkle stars */}
      {AMBIENT_STARS.map((s, i) => (
        <div
          key={i}
          className="fixed pointer-events-none animate-twinkle"
          style={{ top: s.top, left: s.left, animationDelay: `${s.delay}s`, animationDuration: `${2.5 + s.delay}s` }}
          aria-hidden="true"
        >
          <Star
            style={{ width: s.size * 4, height: s.size * 4 }}
            className="text-gold/30 fill-gold/20"
          />
        </div>
      ))}

      <div className="relative z-10 max-w-lg w-full text-center space-y-8">

        {/* Hero icon with burst animation */}
        <div className="flex justify-center">
          <div className="relative">
            {/* Burst particles — rays shooting outward */}
            {!burstDone && BURST_PARTICLES.map((p, i) => (
              <div
                key={i}
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                aria-hidden="true"
                style={{ transform: `rotate(${p.angle}deg)` }}
              >
                <div
                  style={{
                    width: p.size * 2,
                    height: p.size * 2,
                    borderRadius: '50%',
                    background: p.color,
                    animation: `burst-ray 0.9s cubic-bezier(0,0.5,0.5,1) ${p.delay}s forwards`,
                    transformOrigin: '50% 50%',
                  }}
                />
              </div>
            ))}

            {/* Main icon */}
            <div
              className="relative w-20 h-20 rounded-3xl bg-gradient-to-br from-violet-deep to-violet-dark border border-violet/30 flex items-center justify-center animate-fade-in"
              style={{ boxShadow: '0 0 40px rgba(107,33,168,0.5), 0 0 80px rgba(107,33,168,0.2)' }}
            >
              <span className="text-4xl text-gold text-glow-gold animate-pulse-gold" aria-hidden="true">✦</span>
            </div>

            {/* Pulsing outer ring */}
            <div
              className="absolute inset-0 rounded-3xl animate-ping"
              style={{ background: 'transparent', boxShadow: '0 0 0 2px rgba(245,158,11,0.3)', animationDuration: '2s' }}
              aria-hidden="true"
            />
          </div>
        </div>

        {/* Heading */}
        <div className="space-y-3 animate-fade-in" style={{ animationDelay: '0.15s' }}>
          <h1 className="font-display text-star-white text-4xl font-light tracking-wide">
            Welcome to Premium
          </h1>
          <p className="text-gold font-mono text-xs tracking-widest">✦ शुभ समय — अनलिमिटेड ✦</p>
          <p className="text-star-soft text-base leading-relaxed max-w-sm mx-auto">
            Your cosmic toolkit is fully unlocked. Dive into your chart with complete Vedic astrology insights.
          </p>
        </div>

        {/* Feature checklist */}
        <div
          className="bg-cosmic-nebula/60 ring-1 ring-violet/20 rounded-2xl p-6 text-left space-y-3 animate-fade-in"
          style={{ animationDelay: '0.3s' }}
        >
          <p className="text-star-dim text-2xs font-mono uppercase tracking-widest mb-4">
            Now available to you
          </p>
          {PREMIUM_FEATURES.map((f, i) => (
            <div
              key={f}
              className="flex items-center gap-3 animate-slide-in-left"
              style={{ animationDelay: `${0.35 + i * 0.07}s` }}
            >
              <div className="w-5 h-5 rounded-full bg-auspicious/15 border border-auspicious/30 flex items-center justify-center shrink-0">
                <Star className="w-2.5 h-2.5 text-auspicious fill-auspicious" aria-hidden="true" />
              </div>
              <span className="text-star-soft text-sm">{f}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="space-y-3 animate-fade-in" style={{ animationDelay: '0.8s' }}>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-xl
                       bg-gradient-to-r from-violet to-violet-mid
                       text-star-white font-medium text-sm
                       hover:shadow-glow-violet transition-all duration-base active:scale-[0.98]"
          >
            Go to Dashboard
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>

          <p className="text-star-dim text-xs" aria-live="polite">
            Redirecting in {countdown}s…
          </p>

          {syncing && (
            <p className="flex items-center justify-center gap-1.5 text-star-dim text-xs" aria-live="polite">
              <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
              Syncing your account…
            </p>
          )}
        </div>

        {/* Support note */}
        <p className="text-star-dim text-xs animate-fade-in" style={{ animationDelay: '1s' }}>
          Questions? Email{' '}
          <a
            href="mailto:support@shubhtime.in"
            className="text-star-muted hover:text-star-soft transition-colors underline underline-offset-2"
          >
            support@shubhtime.in
          </a>
        </p>
      </div>

      {/* Inline keyframes for burst-ray animation */}
      <style>{`
        @keyframes burst-ray {
          0%   { transform: translateY(0) scale(1); opacity: 0.9; }
          60%  { opacity: 0.6; }
          100% { transform: translateY(-55px) scale(0); opacity: 0; }
        }
      `}</style>
    </div>
  )
}
